/**
 * Safely decodes a URI-encoded string, returning the original if decoding fails.
 * This provides backwards compatibility with cookies set before encoding was added.
 * @param {string} value - The potentially encoded value.
 * @returns {string} The decoded value, or original if decoding fails.
 */
function safeDecodeURIComponent(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    // Value contains invalid % sequences (e.g., "50% off" from old cookies)
    // Return as-is for backwards compatibility
    return value;
  }
}

/**
 * Sets a cookie with the specified name, value, and expiration time.
 * @param {string} cname - The name of the cookie.
 * @param {string} cvalue - The value of the cookie.
 * @param {number} exdays - The number of days until the cookie expires.
 */
function setCookie(cname, cvalue, exdays) {
  let expires, d;
  d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  expires = "expires=" + d.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

/**
 * Retrieves the value of a cookie by its name.
 * Handles both URL-encoded (new) and raw (legacy) cookie values.
 * @param {string} cname - The name of the cookie to retrieve.
 * @returns {string} The value of the cookie, or empty string if not found.
 */
function getCookie(cname) {
  let i, name, ca, c;
  name = cname + "=";
  ca = document.cookie.split(";");
  for (i = 0; i < ca.length; i++) {
    c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      const rawValue = c.substring(name.length, c.length);
      return safeDecodeURIComponent(rawValue);
    }
  }
  return "";
}

/**
 * Sets a cookie with SameSite=Strict and Secure attributes for enhanced security.
 * Values are URL-encoded for RFC 6265 compliance and safe handling of special characters.
 * The Secure flag ensures cookies are only transmitted over HTTPS (OWASP best practice).
 * @param {string} cname - The name of the cookie.
 * @param {string} cvalue - The value of the cookie.
 * @param {number} exdays - The number of days until the cookie expires.
 */
function setSameSiteCookie(cname, cvalue, exdays) {
  let expires, d;
  d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  expires = "expires=" + d.toUTCString();

  // Add Secure flag for HTTPS environments (OWASP security best practice)
  const isHttps = window.location.protocol === "https:";
  const secureFlag = isHttps ? ";Secure" : "";

  // URL-encode value for RFC 6265 compliance (handles spaces, semicolons, unicode)
  const encodedValue = encodeURIComponent(cvalue);

  document.cookie =
    cname +
    "=" +
    encodedValue +
    ";" +
    expires +
    ";path=/;SameSite=Strict" +
    secureFlag;
}

/**
 * Deletes a cookie by setting its expiration to a past date.
 * @param {string} cname - The name of the cookie to delete.
 */
function deleteCookie(cname) {
  document.cookie = cname + "=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/";
}

export { getCookie, setCookie, setSameSiteCookie, deleteCookie };
